// photoHelpers.js - Helper-Funktionen für Photo-Objekte
// Ermöglicht Kompatibilität zwischen alten (String) und neuen (Objekt) Foto-Formaten

/**
 * Prüft ob ein Foto ein String (alt) oder Objekt (neu) ist
 */
export const isPhotoObject = (photo) => {
  return photo && typeof photo === 'object' && photo.url;
};

/**
 * Holt die URL aus einem Foto (egal ob String oder Objekt)
 */
export const getPhotoUrl = (photo) => {
  if (!photo) return null;
  return typeof photo === 'string' ? photo : photo.url;
};

/**
 * Erstellt ein Photo-Objekt mit allen Metadaten
 */
export const createPhotoObject = (url, options = {}) => {
  return {
    url: url,
    status: options.status || 'pending',  // 'pending' oder 'approved'
    uploadedBy: options.uploadedBy || 'Unbekannt',
    uploadedById: options.uploadedById || null,
    uploadedAt: options.uploadedAt || new Date().toISOString(),
    approvedBy: options.approvedBy || null,
    approvedAt: options.approvedAt || null,
    context: options.context || 'review'  // 'review' oder 'main'
  };
};

/**
 * Filtert nur freigegebene Fotos (für normale User)
 */
export const getApprovedPhotos = (photos) => {
  if (!photos || !Array.isArray(photos)) return [];
  
  return photos
    .map(photo => {
      // Alte Fotos (Strings) sind automatisch approved
      if (typeof photo === 'string') {
        return photo;
      }
      
      // Neue Fotos (Objekte): nur approved zurückgeben
      if (photo.status === 'approved') {
        return photo.url;
      }
      
      return null;
    })
    .filter(url => url !== null);
};

/**
 * Holt alle Fotos mit Status (für Admins)
 */
export const getAllPhotosWithStatus = (photos) => {
  if (!photos || !Array.isArray(photos)) return [];
  
  return photos.map(photo => {
    // Alte Fotos zu Objekt-Format konvertieren
    if (typeof photo === 'string') {
      return {
        url: photo,
        status: 'approved',  // Alte Fotos sind automatisch approved
        uploadedBy: 'Legacy',
        uploadedAt: null,
        isLegacy: true
      };
    }
    
    return photo;
  });
};

/**
 * Migration: Konvertiert alte String-Arrays zu neuen Photo-Objekten
 */
export const migratePhotosToObjects = (photos, autoApprove = true) => {
  if (!photos || !Array.isArray(photos)) return [];
  
  return photos.map(photo => {
    // Bereits Objekt? Zurückgeben
    if (typeof photo === 'object' && photo.url) {
      return photo;
    }
    
    // String zu Objekt konvertieren
    return createPhotoObject(photo, {
      status: autoApprove ? 'approved' : 'pending',
      uploadedBy: 'Migration',
      context: 'main'
    });
  });
};

/**
 * Zählt pending Fotos in einer Toilette
 */
export const countPendingPhotos = (photos) => {
  if (!photos || !Array.isArray(photos)) return 0;
  
  return photos.filter(photo => {
    if (typeof photo === 'object' && photo.status === 'pending') {
      return true;
    }
    return false;
  }).length;
};

/**
 * Holt alle pending Fotos aus allen Toiletten
 */
export const getAllPendingPhotos = (toilets) => {
  const pendingPhotos = [];
  
  toilets.forEach(toilet => {
    if (!toilet.images || !Array.isArray(toilet.images)) return;
    
    toilet.images.forEach((photo, index) => {
      if (typeof photo === 'object' && photo.status === 'pending') {
        pendingPhotos.push({
          ...photo,
          toiletId: toilet.id,
          toiletName: toilet.name,
          toiletAddress: toilet.address,
          toiletCity: toilet.city,
          photoIndex: index
        });
      }
    });
    
    // Auch in Kommentaren nach pending Fotos suchen
    if (toilet.comments && Array.isArray(toilet.comments)) {
      toilet.comments.forEach((comment, commentIndex) => {
        if (comment.photos && Array.isArray(comment.photos)) {
          comment.photos.forEach((photo, photoIndex) => {
            if (typeof photo === 'object' && photo.status === 'pending') {
              pendingPhotos.push({
                ...photo,
                toiletId: toilet.id,
                toiletName: toilet.name,
                toiletAddress: toilet.address,
                toiletCity: toilet.city,
                fromComment: true,
                commentIndex: commentIndex,
                commentUser: comment.user,
                photoIndex: photoIndex
              });
            }
          });
        }
      });
    }
  });
  
  return pendingPhotos;
};